/**
 * Port of Pages/Shared/_StatusBadgePartial.cshtml. The mapping covers verification statuses,
 * client statuses, fraud-alert statuses and severities, and audit-log severities.
 */
export function statusBadgeClass(status: string): string {
  switch (status) {
    case 'Approved':
    // A document that passed its checks (IdentityDocument status), green like an approval.
    case 'Verified':
      return 'badge-approved';
    case 'Declined':
      return 'badge-declined';
    case 'Pending':
      return 'badge-pending';
    case 'InProgress':
      return 'badge-inprogress';
    case 'Expired':
      return 'badge-expired';
    case 'Cancelled':
      return 'badge-cancelled';
    case 'Active':
      return 'badge-approved';
    case 'Suspended':
    case 'PendingPayment':
      return 'badge-pending';
    case 'Deactivated':
      return 'badge-declined';
    case 'Open':
    case 'UnderInvestigation':
      return 'badge-pending';
    case 'Resolved':
      return 'badge-approved';
    case 'FalsePositive':
      return 'badge-expired';
    case 'Escalated':
      return 'badge-declined';
    case 'Low':
      return 'badge-low';
    case 'Medium':
      return 'badge-medium';
    case 'High':
      return 'badge-high';
    case 'Critical':
      return 'badge-critical';
    // Audit-log severities
    case 'Info':
      return 'badge-sev-info';
    case 'Warning':
      return 'badge-sev-warning';
    case 'Security':
      return 'badge-sev-security';
    default:
      return 'bg-secondary';
  }
}

/** A subtle border in the badge's own hue (same table as _StatusBadgePartial.cshtml). */
const BORDER_CLASS: Record<string, string> = {
  'badge-approved': 'border-success-subtle',
  'badge-low': 'border-success-subtle',
  'badge-declined': 'border-danger-subtle',
  'badge-high': 'border-orange-subtle',
  'badge-critical': 'border-danger-subtle',
  'badge-pending': 'border-warning-subtle',
  'badge-medium': 'border-warning-subtle',
  'badge-sev-warning': 'border-warning-subtle',
  'badge-inprogress': 'border-primary-subtle',
  'badge-sev-info': 'border-info-subtle',
  'badge-sev-security': 'border-violet-subtle',
};

export function statusBorderClass(badgeClass: string): string {
  return BORDER_CLASS[badgeClass] ?? 'border-secondary-subtle';
}

/**
 * Wording only: the value stays "Expired" in the database, filters, public API and webhooks.
 * "Expired" read too much like the Document Expired signal (same table as _StatusBadgePartial.cshtml).
 */
const STATUS_LABEL: Record<string, string> = {
  Expired: 'Not completed',
};

export function statusLabel(status: string): string {
  return STATUS_LABEL[status] ?? status;
}

export function StatusBadge({ status }: { status: string }) {
  const cls = statusBadgeClass(status);
  return <span className={`badge badge-status ${cls} border ${statusBorderClass(cls)}`}>{statusLabel(status)}</span>;
}
